import { jsx } from 'react/jsx-runtime';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import classNames from 'classnames';
import { forwardRef, useRef } from 'react';
import usePopperPresence from '../../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../../hooks/usePreventTransitionInteraction.mjs';
import { ShakingAnimation } from '../../layout/ShakingAnimation.mjs';
import { ScrollArea } from '../../scroll-area/ScrollArea.mjs';
import { usePopoverContext, PopoverProvider } from '../context.mjs';
import { DEFAULT_COLLISION_PADDING } from '../shared/constants.mjs';
import { content, contentInner, contentMinWidth } from './clickPopover.vanilla.mjs';

const ClickPopover = (props) => {
  const {
    open: openProp,
    defaultOpen,
    onOpenChange,
    modal = true,
    onClose,
    ...rootProps
  } = props;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const { ref: presenceRef } = usePopperPresence({
    open,
    onPresenceChange: (isPresent) => !isPresent && onClose?.()
  });
  return /* @__PURE__ */ jsx(PopoverProvider, { open, presenceRef, children: /* @__PURE__ */ jsx(
    PopoverPrimitive.Root,
    {
      modal,
      open,
      onOpenChange: setOpen,
      ...rootProps
    }
  ) });
};
function ShakingAnimationInner({
  children,
  withScrollArea,
  className: classNameFromProps,
  css
}) {
  if (withScrollArea) {
    return /* @__PURE__ */ jsx(StyledDiv, { className: contentInner, children: /* @__PURE__ */ jsx(ScrollArea, { children: /* @__PURE__ */ jsx(
      StyledViewport,
      {
        css,
        className: classNames(contentMinWidth, classNameFromProps),
        children
      }
    ) }) });
  }
  return /* @__PURE__ */ jsx(
    StyledDiv,
    {
      css,
      className: classNames(contentInner, contentMinWidth, classNameFromProps),
      children
    }
  );
}
const StyledContent = styled(PopoverPrimitive.Content);
const StyledDiv = styled("div");
const StyledViewport = styled(ScrollArea.Viewport_Legacy);
const ClickPopoverContent = forwardRef(
  ({
    sideOffset = 4,
    css,
    children,
    className,
    style: styleFromProps,
    prefersReducedMotion,
    zIndex,
    closeOnInteractOutside = true,
    collisionPadding = DEFAULT_COLLISION_PADDING,
    onInteractOutside,
    withScrollArea,
    ...props
  }, forwardedRef) => {
    const { open, presenceRef } = usePopoverContext("ClickPopover.Content");
    const composedRefs = useComposedRefs(presenceRef, forwardedRef);
    const animationRef = useRef(null);
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    return /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(
      StyledContent,
      {
        sideOffset,
        ref: composedRefs,
        collisionPadding,
        className: content({ prefersReducedMotion }),
        style: Object.assign({}, zIndexStyle, styleFromProps),
        ...props,
        onInteractOutside: composeEventHandlers(onInteractOutside, (event) => {
          if (!closeOnInteractOutside) {
            animationRef.current?.triggerAnimation();
            event.preventDefault();
          }
        }),
        children: /* @__PURE__ */ jsx(ShakingAnimation, { ref: animationRef, children: /* @__PURE__ */ jsx(
          ShakingAnimationInner,
          {
            withScrollArea,
            className,
            css,
            children
          }
        ) })
      }
    ) });
  }
);
const ClickPopoverContent_Legacy = forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ jsx(ClickPopoverContent, { ref: forwardedRef, ...props, withScrollArea: true });
});
const ClickPopoverContent_New = forwardRef((props, forwardedRef) => {
  const { open } = usePopoverContext("ClickPopover.Content");
  const preventTransitionInteraction = usePreventTransitionInteraction({
    open
  });
  return /* @__PURE__ */ jsx(
    ClickPopoverContent,
    {
      ref: forwardedRef,
      ...props,
      onKeyDownCapture: (e) => {
        props.onKeyDownCapture?.(e);
        preventTransitionInteraction.onKeyDownCapture?.(e);
      },
      style: {
        ...props.style,
        ...preventTransitionInteraction.style
      },
      withScrollArea: false
    }
  );
});
ClickPopover.Trigger = PopoverPrimitive.Trigger;
ClickPopover.Close = PopoverPrimitive.Close;
ClickPopover.Content = ClickPopoverContent_New;
ClickPopover.Content_Legacy = ClickPopoverContent_Legacy;
ClickPopover.Anchor = PopoverPrimitive.Anchor;

export { ClickPopover };
//# sourceMappingURL=ClickPopover.mjs.map
